import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../client/config';
import { brandingAssetUrl, DEFAULT_BRANDING_FAVICON, type OrganizationBrandingData } from '../components/OrganizationBranding';
import { Logo } from '../components/Logo';
import { Button, Field, Panel } from '../components/ui';
import { PageContainer } from '../nav/PageContainer';
import { PageTitle } from '../nav/PageTitle';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';

type AssetKind = 'logo' | 'favicon';
type AssetUpload = { mimeType: string; base64: string; preview: string; name: string };
type AssetDraft = AssetUpload | null | undefined;
const LIMITS = { logo: 1024 * 1024, favicon: 256 * 1024 };
const LABELS = { logo: '로고', favicon: '파비콘' };

async function readImage(file: File, kind: AssetKind): Promise<AssetUpload> {
  if (!file.size || file.size > LIMITS[kind]) throw new Error(`${LABELS[kind]} 파일은 ${kind === 'logo' ? '1MiB' : '256KiB'} 이하로 선택하세요.`);
  const result = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('파일을 읽지 못했습니다.'));
    reader.onerror = () => reject(new Error('파일을 읽지 못했습니다.'));
    reader.readAsDataURL(file);
  });
  const base64 = result.slice(result.indexOf(',') + 1);
  const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value);
  const jpeg = bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
  const webp = String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP';
  const ico = bytes[0] === 0 && bytes[1] === 0 && bytes[2] === 1 && bytes[3] === 0 && bytes.length >= 6 && (bytes[4] !== 0 || bytes[5] !== 0);
  const mimeType = png ? 'image/png' : kind === 'logo' && jpeg ? 'image/jpeg' : kind === 'logo' && webp ? 'image/webp' : kind === 'favicon' && ico ? 'image/x-icon' : null;
  const declaredType = file.type === 'image/vnd.microsoft.icon' ? 'image/x-icon' : file.type;
  if (!mimeType || (declaredType && declaredType !== mimeType)) throw new Error(`${kind === 'logo' ? '로고는 PNG, JPEG, WebP' : '파비콘은 PNG, ICO'} 이미지만 사용할 수 있습니다.`);
  const preview = `data:${mimeType};base64,${base64}`;
  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    const timer = window.setTimeout(() => { image.onload = null; image.onerror = null; reject(new Error('이미지 확인 시간이 초과됐습니다.')); }, 10000);
    image.onload = () => {
      window.clearTimeout(timer);
      if (image.naturalWidth > 0 && image.naturalHeight > 0) resolve();
      else reject(new Error('유효한 이미지가 아닙니다.'));
    };
    image.onerror = () => { window.clearTimeout(timer); reject(new Error('이미지를 열 수 없습니다. 다른 파일을 선택하세요.')); };
    image.src = preview;
  });
  return { mimeType, base64, preview, name: file.name };
}

export function OrganizationDesign({ orgId }: { orgId?: string }): JSX.Element {
  return <OrganizationDesignForm key={orgId ?? 'current'} selectedOrgId={orgId} />;
}

function OrganizationDesignForm({ selectedOrgId }: { selectedOrgId: string | undefined }): JSX.Element {
  const endpoint = `${API_BASE}/api/branding/${selectedOrgId ? encodeURIComponent(selectedOrgId) : 'current'}`;
  const [data, setData] = useState<OrganizationBrandingData | null>(null);
  const [orgName, setOrgName] = useState('');
  const [drafts, setDrafts] = useState<Record<AssetKind, AssetDraft>>({ logo: undefined, favicon: undefined });
  const [validating, setValidating] = useState<Record<AssetKind, boolean>>({ logo: false, favicon: false });
  const [assetErrors, setAssetErrors] = useState<Partial<Record<AssetKind, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resetReview, setResetReview] = useState(false);
  const [previewFailures, setPreviewFailures] = useState<Partial<Record<AssetKind, string>>>({});
  const mounted = useRef(false);
  const savePending = useRef(false);
  const request = useRef<AbortController | null>(null);
  const fileVersions = useRef({ logo: 0, favicon: 0 });

  const load = useCallback(async (): Promise<boolean> => {
    request.current?.abort();
    const controller = new AbortController(); request.current = controller;
    setLoading(true); setData(null); setError(null);
    fileVersions.current.logo++; fileVersions.current.favicon++;
    setValidating({ logo: false, favicon: false });
    try {
      const response = await fetch(endpoint, { credentials: 'include', signal: controller.signal });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error || `디자인 조회 실패 (HTTP ${response.status})`);
      }
      let next = await response.json() as OrganizationBrandingData;
      if (controller.signal.aborted || !mounted.current) return false;
      if (!selectedOrgId) {
        const currentOrgId = next.orgId;
        const configResponse = await fetch(`${API_BASE}/api/branding/${encodeURIComponent(currentOrgId)}`, { credentials: 'include', signal: controller.signal });
        if (!configResponse.ok) throw new Error(`조직 디자인 조회 실패 (HTTP ${configResponse.status})`);
        next = await configResponse.json() as OrganizationBrandingData;
        if (controller.signal.aborted || !mounted.current) return false;
        if (next.orgId !== currentOrgId) throw new Error('현재 조직 정보를 다시 확인해 주세요.');
      }
      if (selectedOrgId && next.orgId !== selectedOrgId) throw new Error('선택한 조직과 응답이 다릅니다. 다시 불러오세요.');
      setData(next); setOrgName(next.orgName); setDrafts({ logo: undefined, favicon: undefined });
      setAssetErrors({}); setPreviewFailures({}); setResetReview(false);
      return true;
    } catch (reason) {
      if (!controller.signal.aborted && mounted.current) setError(reason instanceof Error ? reason.message : String(reason));
      return false;
    } finally { if (!controller.signal.aborted && mounted.current) setLoading(false); }
  }, [endpoint, selectedOrgId]);

  useEffect(() => {
    mounted.current = true; void load();
    return () => { mounted.current = false; request.current?.abort(); fileVersions.current.logo++; fileVersions.current.favicon++; };
  }, [load]);

  async function selectFile(kind: AssetKind, file: File): Promise<void> {
    const version = ++fileVersions.current[kind];
    setSuccess(null); setValidating((state) => ({ ...state, [kind]: true }));
    setAssetErrors((state) => ({ ...state, [kind]: '' }));
    try {
      const upload = await readImage(file, kind);
      if (mounted.current && version === fileVersions.current[kind]) {
        setDrafts((state) => ({ ...state, [kind]: upload }));
        setPreviewFailures((state) => ({ ...state, [kind]: '' }));
      }
    } catch (reason) {
      if (mounted.current && version === fileVersions.current[kind]) setAssetErrors((state) => ({ ...state, [kind]: reason instanceof Error ? reason.message : String(reason) }));
    } finally {
      if (mounted.current && version === fileVersions.current[kind]) setValidating((state) => ({ ...state, [kind]: false }));
    }
  }

  const busy = loading || saving || validating.logo || validating.favicon;
  const dirty = !!data && (orgName !== data.orgName || drafts.logo !== undefined || drafts.favicon !== undefined);
  const assetPayload = (draft: AssetDraft) => draft ? { mimeType: draft.mimeType, base64: draft.base64 } : null;

  async function persist(reset = false): Promise<void> {
    if (!data?.canManage || busy || savePending.current || (!reset && (!orgName.trim() || Object.values(assetErrors).some(Boolean)))) return;
    savePending.current = true; setSaving(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${API_BASE}/api/branding/${encodeURIComponent(data.orgId)}`, {
        method: reset ? 'DELETE' : 'PUT', credentials: 'include',
        ...(reset ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ orgName: orgName.trim(),
          ...(drafts.logo !== undefined ? { logo: assetPayload(drafts.logo) } : {}),
          ...(drafts.favicon !== undefined ? { favicon: assetPayload(drafts.favicon) } : {}),
        }) }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(body?.error || `디자인 저장 실패 (HTTP ${response.status})`);
      }
      window.dispatchEvent(new Event('bricklit:scope-changed'));
      if (!mounted.current) return;
      const refreshed = await load();
      if (mounted.current) setSuccess(`${reset ? '기본 디자인으로 초기화했습니다.' : '조직 디자인을 저장했습니다.'}${refreshed ? '' : ' 최신 설정은 다시 불러와 주세요.'}`);
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason.message : String(reason));
    } finally { savePending.current = false; if (mounted.current) setSaving(false); }
  }

  return <PageContainer fluid className="bg-bg text-text">
    <PageTitle className="mb-0" title="조직 디자인" />
    <p className="text-[14px] text-text-dim">저장한 이름·로고·파비콘은 이 조직 전체에 적용됩니다. 미리보기를 확인한 뒤 저장하세요.</p>
    {error && <p role="alert" className="rounded border border-red/50 bg-red/15 p-3 text-red">{error}</p>}
    {success && <p role="status" className="rounded border border-line bg-panel-2 p-3">{success}</p>}
    {loading && !data && <ContentSkeleton variant="form" label="조직 디자인을 불러오는 중…" />}
    {!data && !loading && <Button onClick={() => void load()}>다시 불러오기</Button>}
    {data && <>
      {!data.canManage && <p role="status" className="text-[14px] text-text-dim">현재 계정은 조직 디자인을 조회할 수 있습니다. 변경 권한은 없습니다.</p>}
      {data.isBo && <p className="text-[13px] text-text-dim">선택한 조직의 디자인 미리보기입니다. 운영자 헤더와 브라우저 아이콘은 BRICK 기본 디자인을 유지합니다.</p>}
      <form onSubmit={(event) => { event.preventDefault(); void persist(); }} aria-busy={busy}>
        <fieldset disabled={busy || !data.canManage} className="min-w-0 space-y-5">
          <Field label="조직 이름" value={orgName} required onChange={(event) => { setOrgName(event.target.value); setSuccess(null); }} />
          <div className="grid gap-4 md:grid-cols-2">
            {(['logo', 'favicon'] as const).map((kind) => {
              const draft = drafts[kind];
              const stored = brandingAssetUrl(kind === 'logo' ? data.logoUrl : data.faviconUrl);
              const source = draft === null ? null : draft?.preview ?? stored;
              const failed = !!source && previewFailures[kind] === source;
              return <Panel key={kind} title={`${LABELS[kind]} 미리보기`}>
                <div className="mb-3 flex min-h-[120px] items-center justify-center gap-3 rounded border border-line bg-panel-2 p-4">
                  {source && !failed ? <img src={source} alt={`${LABELS[kind]} 미리보기`} className="max-h-20 max-w-full object-contain"
                    onError={() => setPreviewFailures((state) => ({ ...state, [kind]: source }))} />
                    : kind === 'logo' ? <Logo size={28} /> : <img src={DEFAULT_BRANDING_FAVICON} alt="기본 파비콘" width={32} height={32} />}
                  {kind === 'logo' && <span className="max-w-[180px] break-words font-semibold">{orgName || '조직 이름'}</span>}
                </div>
                {failed && <p role="status" className="mb-2 text-[13px] text-text-dim">이미지를 불러오지 못해 기본 디자인을 표시합니다.</p>}
                <label className="block text-[14px]">{LABELS[kind]} 파일 선택
                  <input type="file" accept={kind === 'logo' ? 'image/png,image/jpeg,image/webp' : 'image/png,image/x-icon,image/vnd.microsoft.icon,.ico'}
                    aria-describedby={`${kind}-requirements`} className="mt-2 block w-full rounded border border-line bg-panel-2 p-2 text-[13px] text-text"
                    onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) void selectFile(kind, file); }} />
                </label>
                <p id={`${kind}-requirements`} className="mt-1 text-[12px] text-text-dim">{kind === 'logo' ? 'PNG / JPEG / WebP · 최대 1MiB' : 'PNG / ICO · 최대 256KiB'}</p>
                {validating[kind] && <p role="status" className="mt-2 text-[13px]">이미지를 확인하는 중…</p>}
                {assetErrors[kind] && <p role="alert" className="mt-2 text-[13px] text-red">{assetErrors[kind]}</p>}
                {draft && <p className="mt-2 break-all text-[13px] text-text-dim">선택: {draft.name} · 저장 전</p>}
                {draft === null && <p className="mt-2 text-[13px] text-text-dim">저장하면 기본 {LABELS[kind]}로 돌아갑니다.</p>}
                <Button type="button" className="mt-3" disabled={!source && !assetErrors[kind]} onClick={() => {
                  fileVersions.current[kind]++; setDrafts((state) => ({ ...state, [kind]: null }));
                  setAssetErrors((state) => ({ ...state, [kind]: '' })); setSuccess(null);
                }}>{LABELS[kind]} 제거</Button>
              </Panel>;
            })}
          </div>
          {data.canManage && <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={!dirty || !orgName.trim() || Object.values(assetErrors).some(Boolean)}>{saving ? '저장 중…' : '조직 전체에 저장'}</Button>
            <Button type="button" disabled={!dirty && !Object.values(assetErrors).some(Boolean)} onClick={() => { setOrgName(data.orgName); setDrafts({ logo: undefined, favicon: undefined }); setAssetErrors({}); setSuccess(null); }}>변경 취소</Button>
            <Button type="button" onClick={() => setResetReview(true)}>기본 디자인 초기화</Button>
          </div>}
          {resetReview && data.canManage && <div role="group" aria-label="디자인 초기화 확인" className="rounded border border-line bg-panel-2 p-4 text-[14px]">
            <p>저장하지 않은 변경을 버리고 로고·파비콘을 기본 디자인으로 초기화합니다.</p>
            <div className="mt-3 flex gap-2"><Button type="button" onClick={() => setResetReview(false)}>초기화 취소</Button>
              <Button type="button" variant="danger" onClick={() => void persist(true)}>디자인 초기화 확인</Button></div>
          </div>}
        </fieldset>
      </form>
    </>}
  </PageContainer>;
}
